'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { globalErrorHandler } from '@/lib/utils/global-error-handler';

export default function Error({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    // Log error to global error handler
    globalErrorHandler.reportError(error, {
      digest: error.digest,
      page: 'app/error.tsx'
    });
  }, [error]);

  return (
    <div className="flex h-[calc(100vh-4rem)] items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-destructive h-5 w-5" />
            <CardTitle>Something went wrong!</CardTitle>
          </div>
          <CardDescription>
            An error occurred while loading this page. Don't worry, your data is
            safe.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted rounded-lg p-3 text-sm">
            <code className="text-muted-foreground">
              {error.message || 'An unexpected error occurred'}
            </code>
          </div>

          <div className="flex gap-2">
            <Button size="xs" density="compact" gap="standard" onClick={reset}>
              <RefreshCw className="h-3.5 w-3.5" />
              Try Again
            </Button>
            <Button
              size="xs"
              density="compact"
              gap="standard"
              variant="outline"
              onClick={() => router.push('/')}
            >
              <Home className="h-3.5 w-3.5" />
              Go to Dashboard
            </Button>
          </div>

          {error.digest && (
            <p className="text-muted-foreground text-center text-xs">
              Error ID: {error.digest}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
